import { useCallback, useState } from "react";

import {
  ProvidersService,
  type ProviderCredentialResponse,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError, unwrapNestedErrorMessage } from "@/lib/api-error";
import {
  extractRotatedKey,
  formatMaskedKey,
  formatProviderLabel,
} from "@/lib/provider-helpers";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface Props {
  credential: ProviderCredentialResponse | null;
  onClose: () => void;
  onRotated: (data: {
    credential: ProviderCredentialResponse;
    plaintext: string;
  }) => void;
}

/** Rotate the API key on an existing credential. The backend
 *  re-validates the new key upstream + responds with the
 *  ``api_key_plaintext`` populated; we then open the show-once
 *  dialog with the new value. ``extractRotatedKey`` guards
 *  against the metadata-only PATCH case where the field comes
 *  back as the empty string. */
export function ProviderRotateDialog({
  credential,
  onClose,
  onRotated,
}: Props) {
  const { t } = useTranslation();
  const [newKey, setNewKey] = useState("");

  const mutator = useCallback(
    (payload: { id: string; key: string }) =>
      ProvidersService.rotateKey(payload.id, payload.key),
    []
  );
  const { mutate, isLoading, error, reset } = useMutation<
    { id: string; key: string },
    ProviderCredentialResponse
  >(mutator, {
    onSuccess: (next) => {
      const plaintext = extractRotatedKey(next);
      if (!plaintext) return; // defensive — backend always returns it on rotate
      onRotated({ credential: next, plaintext });
      setNewKey("");
    },
  });

  if (!credential) return null;

  const handleClose = () => {
    reset();
    setNewKey("");
    onClose();
  };

  const trimmedKey = newKey.trim();
  const keyValid = trimmedKey.length >= 8;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyValid || isLoading) return;
    await mutate({ id: credential.id, key: trimmedKey });
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("providers.rotateDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("providers.rotateDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="rounded-md border border-ocean-100 bg-ocean-50/40 p-3 text-sm">
            <div className="label-eyebrow mb-1">
              {t("providers.rotateDialog.credentialLabel")}
            </div>
            <div className="font-medium text-ocean-900">
              {formatProviderLabel(credential.provider_type)} ·{" "}
              {credential.credential_name}
            </div>
            <div className="mt-1 text-xs text-neutral-600">
              {t("providers.rotateDialog.currentKeyLabel")}{" "}
              <code className="text-ocean-700">
                {formatMaskedKey(
                  credential.provider_type,
                  credential.api_key_last_4
                )}
              </code>
            </div>
          </div>

          <FormField
            label={t("providers.rotateDialog.keyLabel")}
            type="password"
            value={newKey}
            onChange={setNewKey}
            placeholder={t("providers.rotateDialog.keyPlaceholder")}
            required
            autoFocus
            error={
              trimmedKey.length > 0 && !keyValid
                ? t("providers.rotateDialog.keyError")
                : undefined
            }
            hint={t("providers.rotateDialog.keyHint")}
          />

          {error && (
            <Alert variant="danger">
              <div className="space-y-1">
                <div className="text-sm font-medium">{parseApiError(error)}</div>
                {error instanceof Error && (
                  <div className="text-xs text-danger-600/80">
                    {t("providers.rotateDialog.providerDetail", {
                      detail: unwrapNestedErrorMessage(error.message),
                    })}
                  </div>
                )}
              </div>
            </Alert>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("providers.rotateDialog.cancel")}
          </Button>
          <Button type="submit" disabled={!keyValid || isLoading}>
            {isLoading
              ? t("providers.rotateDialog.submitLoading")
              : t("providers.rotateDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
